import { BrowserMultiFormatReader, BarcodeFormat } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';
import { isAr, copyText, trackEvent } from './extra-common.js';
import { showAlert, showToast } from '../core/modal.js';

/**
 * Kazakan Tools — Advanced Liquid Glass QR & Barcode Scanner Studio
 * - Live Camera Scanning with animated viewfinder reticle & laser beam
 * - Hardware & Digital Zoom Controls (1x, 2x, 3x, Pinch-to-zoom, Mouse wheel, Double-tap Smart Zoom)
 * - Dual Engine Detection: Hardware-accelerated native BarcodeDetector + Multi-scale ZXing fallback
 * - Distant & Small QR Code Auto-Detection via high-density center crop
 * - 1D & 2D Barcode Support (EAN-13, EAN-8, UPC, Code 128, Code 39, Code 93, ITF, Codabar, Data Matrix, Aztec, PDF417)
 * - Multi-camera detection & seamless switching (Front / Rear / Tele / Wide)
 * - Hardware torch / flashlight toggle
 * - Image file drop, file browse, and instant clipboard paste (Ctrl + V)
 * - Smart Content Parser: URL, Wi-Fi Network, Contact vCard, Product Barcode with Google Search, Email, Phone, SMS, Geo, Plain Text
 * - Audio chime & haptic feedback on successful detection
 * - Recent scan history tray
 * - 100% Client-side privacy
 */

const t = (ar, en) => (isAr ? ar : en);
const root = document.getElementById('qr-scanner-app');

if (root) {
  root.innerHTML = `
    <div class="qr-studio">
      <!-- 1. Top Segmented Mode Switcher (Shared Design System) -->
      <div class="batch-segmented-track qr-modes-nav" role="tablist" aria-label="${t('طريقة المسح', 'Scanning Method')}">
        <button type="button" class="batch-segment-pill is-active" id="qr-tab-camera" role="tab" aria-selected="true" aria-controls="qr-pane-camera">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
          <span>${t('مسح بالكاميرا المباشرة', 'Live Camera Scan')}</span>
        </button>
        <button type="button" class="batch-segment-pill" id="qr-tab-file" role="tab" aria-selected="false" aria-controls="qr-pane-file">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <span>${t('مسح من صورة أو لقطة', 'Scan from Image or File')}</span>
        </button>
      </div>

      <!-- 2. Camera Viewfinder Pane -->
      <div class="qr-pane is-active" id="qr-pane-camera" role="tabpanel" aria-labelledby="qr-tab-camera">
        <div class="qr-viewfinder-box" id="qr-viewfinder">
          <!-- Inactive Themed Splash Overlay -->
          <div class="qr-splash-overlay" id="qr-camera-splash">
            <div class="batch-drop-icon qr-splash-icon-wrap" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
            <h2 class="qr-splash-title">${t('المسح المباشر لكود QR والباركود', 'Live QR & Barcode Scanner')}</h2>
            <p class="qr-splash-desc">${t('وجّه كاميرا هاتفك أو حاسوبك نحو أي رمز QR أو باركود لقراءته فوراً وبأعلى دقة، مع دعم التقريب (Zoom) التلقائي واليدوي لقراءة الرموز الصغيرة والبعيدة.', 'Point your device camera at any QR code or barcode to scan it instantly. Supports digital & optical zoom to easily capture small and distant codes.')}</p>
            <button type="button" class="btn-primary qr-start-cam-btn" id="qr-btn-start">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              <span>${t('تشغيل الكاميرا', 'Start Camera')}</span>
            </button>
            <div class="qr-privacy-tag">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>${t('معالجة فورية محلية 100% · الفيديو لا يغادر جهازك أبداً', '100% local browser processing · Camera video never leaves your device')}</span>
            </div>
          </div>

          <!-- Video Stream -->
          <video id="qr-video" class="qr-video" autoplay playsinline muted hidden></video>

          <!-- Animated Target Reticle with Laser Beam -->
          <div class="qr-reticle" id="qr-reticle" aria-hidden="true" hidden>
            <div class="qr-corner corner-tl"></div>
            <div class="qr-corner corner-tr"></div>
            <div class="qr-corner corner-bl"></div>
            <div class="qr-corner corner-br"></div>
            <div class="qr-laser-line"></div>
          </div>

          <!-- Live Top HUD Toolbar (Shown strictly when camera is streaming) -->
          <div class="qr-hud-top" id="qr-hud-top" hidden>
            <!-- Camera Switcher -->
            <div class="qr-cam-switch-box" id="qr-cam-switch-box" hidden>
              <button type="button" class="qr-hud-btn qr-cam-flip-btn" id="qr-btn-switch-cam" title="${t('تبديل الكاميرا', 'Switch Camera')}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                  <path d="M20 10c0-4.418-3.582-8-8-8s-8 3.582-8 8c0 1.94.693 3.72 1.848 5.115l-1.848 1.885h6v-6l-2.023 2.062c-.886-.97-1.427-2.257-1.427-3.672 0-3.032 2.458-5.49 5.49-5.49s5.49 2.458 5.49 5.49-2.458 5.49-5.49 5.49v2.51c4.418 0 8-3.582 8-8z"/>
                </svg>
                <span id="qr-active-cam-label">${t('تبديل الكاميرا', 'Switch Camera')}</span>
              </button>
              <select id="qr-cam-select" class="qr-cam-dropdown" aria-label="${t('قائمة الكاميرات المتاحة', 'Available Cameras')}"></select>
            </div>

            <div class="qr-hud-actions-right">
              <!-- Torch / Flashlight Toggle -->
              <button type="button" class="qr-hud-btn qr-hud-icon-btn" id="qr-btn-torch" title="${t('تشغيل الفلاش', 'Flashlight')}" aria-label="${t('تشغيل الفلاش', 'Flashlight')}" hidden>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
              </button>

              <!-- Sound Toggle -->
              <button type="button" class="qr-hud-btn qr-hud-icon-btn is-active" id="qr-btn-sound" title="${t('صوت التنبيه عند المسح', 'Scan Beep')}" aria-label="${t('صوت التنبيه', 'Scan Beep')}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>
              </button>

              <!-- Stop Camera Button -->
              <button type="button" class="qr-hud-btn qr-btn-stop" id="qr-btn-stop" title="${t('إيقاف الكاميرا', 'Stop Camera')}">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                  <rect x="6" y="6" width="12" height="12" rx="2.5"/>
                </svg>
                <span>${t('إيقاف', 'Stop')}</span>
              </button>
            </div>
          </div>

          <!-- Live Camera Zoom Controls (Strictly hidden until camera stream is running) -->
          <div class="qr-zoom-controls" id="qr-zoom-controls" hidden aria-label="${t('التحكم في تقريب الكاميرا', 'Camera Zoom Controls')}">
            <button type="button" class="qr-zoom-btn" id="qr-zoom-out" title="${t('تصغير', 'Zoom Out')}" aria-label="${t('تصغير', 'Zoom Out')}">−</button>
            <div class="qr-zoom-presets" id="qr-zoom-presets">
              <button type="button" class="qr-zoom-pill is-active" data-zoom="1">1×</button>
              <button type="button" class="qr-zoom-pill" data-zoom="2">2×</button>
              <button type="button" class="qr-zoom-pill" data-zoom="3">3×</button>
            </div>
            <button type="button" class="qr-zoom-btn" id="qr-zoom-in" title="${t('تكبير', 'Zoom In')}" aria-label="${t('تكبير', 'Zoom In')}">+</button>
          </div>

          <!-- Live Bottom Status Bar (Shown strictly when camera is streaming) -->
          <div class="qr-hud-bottom" id="qr-hud-bottom" hidden>
            <span class="qr-pulse-indicator" aria-hidden="true"></span>
            <span class="qr-hud-status-text" id="qr-live-status-text">${t('جاري البحث عن رمز QR أو باركود... وجّه الكاميرا نحو الرمز', 'Scanning for QR code or barcode... Align in frame')}</span>
          </div>
        </div>
      </div>

      <!-- 3. Image File Upload Pane (Standardized Dropzone System) -->
      <div class="qr-pane" id="qr-pane-file" role="tabpanel" aria-labelledby="qr-tab-file" hidden>
        <div id="qr-dropzone" class="dropzone batch-dropzone" role="button" tabindex="0" aria-label="${t('اسحب صورة رمز QR أو باركود أو انقر لاختيارها من جهازك', 'Drop QR code or barcode image or click to choose from your device')}">
          <input type="file" id="qr-file-input" class="hidden-file-input" accept="image/*">
          <div id="qr-dropzone-default" class="dropzone-content-default">
            <svg class="dropzone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
              <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242M12 12v9m0-9l-3 3m3-3l3 3"/>
            </svg>
            <p class="dropzone-title">${t('اسحب صورة رمز QR أو باركود أو انقر لاختيارها من جهازك', 'Drop QR code or barcode image or click to choose from your device')}</p>
            <p class="dropzone-subtitle">${t('يدعم صور PNG, JPG, WebP أو اللصق المباشر من الحافظة (Ctrl + V) · يتعرف تلقائياً على رموز QR وجميع أنواع الباركود', 'Supports PNG, JPG, WebP or direct clipboard paste (Ctrl + V) · Automatically decodes QR codes & all 1D barcode formats')}</p>
          </div>
        </div>

        <!-- Standard Batch Card for File Preview -->
        <div class="batch-card qr-file-preview-card" id="qr-file-preview-card" hidden>
          <div class="batch-card-main">
            <div class="batch-thumb-wrap">
              <img id="qr-preview-img" alt="${t('معاينة صورة الرمز', 'Code image preview')}" title="${t('معاينة صورة الرمز', 'Code image preview')}" class="batch-thumbnail">
            </div>
            <div class="batch-card-body">
              <div class="batch-card-title-row">
                <span class="batch-file-name" id="qr-preview-filename"></span>
              </div>
              <div class="batch-card-stats">
                <span class="batch-stat-orig" id="qr-preview-filesize"></span>
                <span class="batch-status-tag">${t('تم اختيار الصورة', 'Image loaded')}</span>
              </div>
            </div>
            <div class="batch-card-actions">
              <button type="button" class="btn-secondary btn-sm" id="qr-btn-reset-file">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>${t('تغيير الصورة', 'Change Image')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Smart Interactive Result Card -->
      <section class="qr-smart-card" id="qr-smart-card" hidden aria-label="${t('نتيجة الرمز المفحوص', 'Scanned Code Result')}">
        <div class="qr-result-head">
          <div class="qr-type-badge" id="qr-type-badge">
            <span class="qr-type-icon" id="qr-type-icon"></span>
            <span class="qr-type-title" id="qr-type-title"></span>
          </div>
          <span class="qr-format-pill" id="qr-format-pill">QR_CODE</span>
        </div>

        <div class="qr-result-content" id="qr-result-content"></div>

        <div class="qr-result-actions" id="qr-result-actions"></div>
      </section>

      <!-- 5. Recent Scan History Tray -->
      <details class="qr-history-tray" id="qr-history-tray" hidden>
        <summary class="qr-history-summary">
          <div class="qr-history-title-wrap">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>${t('الرموز الممسوحة مؤخراً في هذه الجلسة', 'Recent Scans in this Session')}</span>
            <span class="qr-history-badge" id="qr-history-badge">0</span>
          </div>
          <svg class="qr-history-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </summary>
        <div class="qr-history-body">
          <ul class="qr-history-list" id="qr-history-list"></ul>
          <div class="qr-history-toolbar">
            <button type="button" class="btn-tertiary btn-sm" id="qr-btn-clear-history">${t('مسح السجل', 'Clear History')}</button>
          </div>
        </div>
      </details>
    </div>
  `;

  initQrStudio();
}

function initQrStudio() {
  // Configure ZXing with all 1D & 2D formats and TRY_HARDER for deep recognition
  const hints = new Map();
  hints.set(DecodeHintType.TRY_HARDER, true);
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.AZTEC,
    BarcodeFormat.PDF_417,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODABAR,
    BarcodeFormat.ITF
  ]);

  const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });

  // Native BarcodeDetector initialization (Hardware-accelerated when available)
  let nativeDetector = null;
  const supportedNativeFormats = [
    'qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'code_93',
    'codabar', 'data_matrix', 'itf', 'pdf417', 'upc_a', 'upc_e', 'aztec'
  ];

  if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
    try {
      nativeDetector = new window.BarcodeDetector({ formats: supportedNativeFormats });
    } catch {
      try {
        nativeDetector = new window.BarcodeDetector();
      } catch {
        nativeDetector = null;
      }
    }
  }

  // Mode Tabs
  const tabCamera = document.getElementById('qr-tab-camera');
  const tabFile = document.getElementById('qr-tab-file');
  const paneCamera = document.getElementById('qr-pane-camera');
  const paneFile = document.getElementById('qr-pane-file');

  // Camera elements
  const viewfinder = document.getElementById('qr-viewfinder');
  const btnStart = document.getElementById('qr-btn-start');
  const btnStop = document.getElementById('qr-btn-stop');
  const video = document.getElementById('qr-video');
  const cameraSplash = document.getElementById('qr-camera-splash');
  const reticle = document.getElementById('qr-reticle');
  const hudTop = document.getElementById('qr-hud-top');
  const hudBottom = document.getElementById('qr-hud-bottom');
  const liveStatusText = document.getElementById('qr-live-status-text');

  // Multi-camera controls
  const camSwitchBox = document.getElementById('qr-cam-switch-box');
  const btnSwitchCam = document.getElementById('qr-btn-switch-cam');
  const camSelect = document.getElementById('qr-cam-select');
  const activeCamLabel = document.getElementById('qr-active-cam-label');
  const btnTorch = document.getElementById('qr-btn-torch');
  const btnSound = document.getElementById('qr-btn-sound');

  // Zoom elements
  const zoomControls = document.getElementById('qr-zoom-controls');
  const zoomOutBtn = document.getElementById('qr-zoom-out');
  const zoomInBtn = document.getElementById('qr-zoom-in');
  const zoomPills = Array.from(document.querySelectorAll('.qr-zoom-pill'));

  // Ensure zoom controls are hidden initially
  if (zoomControls) {
    zoomControls.hidden = true;
    zoomControls.style.display = 'none';
  }

  // File elements
  const dropzone = document.getElementById('qr-dropzone');
  const fileInput = document.getElementById('qr-file-input');
  const filePreviewCard = document.getElementById('qr-file-preview-card');
  const previewImg = document.getElementById('qr-preview-img');
  const previewFilename = document.getElementById('qr-preview-filename');
  const previewFilesize = document.getElementById('qr-preview-filesize');
  const btnResetFile = document.getElementById('qr-btn-reset-file');

  // Result card elements
  const resultCard = document.getElementById('qr-smart-card');
  const typeIcon = document.getElementById('qr-type-icon');
  const typeTitle = document.getElementById('qr-type-title');
  const formatPill = document.getElementById('qr-format-pill');
  const resultContent = document.getElementById('qr-result-content');
  const resultActions = document.getElementById('qr-result-actions');

  // History elements
  const historyTray = document.getElementById('qr-history-tray');
  const historyBadge = document.getElementById('qr-history-badge');
  const historyList = document.getElementById('qr-history-list');
  const btnClearHistory = document.getElementById('qr-btn-clear-history');

  // State variables
  let mediaStream = null;
  let isCameraRunning = false;
  let scanTimer = null;
  let isScanTickBusy = false;
  let availableCameras = [];
  let selectedDeviceId = localStorage.getItem('kz_qr_camera_id') || null;
  let currentCameraIndex = 0;
  let isTorchOn = false;
  let isSoundEnabled = true;
  let lastScannedText = null;
  let lastScannedTime = 0;
  let currentFileUrl = null;
  const historyItems = [];

  // Zoom state
  let currentZoom = 1;
  let minZoom = 1;
  let maxZoom = 4;
  let zoomStep = 0.2;
  let hasHardwareZoom = false;

  // Dedicated offscreen processing canvases:
  // 1. fullCanvas: full aspect ratio for wide 1D barcodes (EAN-13, UPC, Code 128)
  const fullCanvas = document.createElement('canvas');
  const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true });

  // 2. zoomCanvas: center-crop magnified canvas for small QR codes & distant barcodes
  const zoomCanvas = document.createElement('canvas');
  const zoomCtx = zoomCanvas.getContext('2d', { willReadFrequently: true });

  // Tab switching
  function switchTab(mode) {
    if (mode === 'camera') {
      tabCamera.classList.add('is-active');
      tabCamera.setAttribute('aria-selected', 'true');
      tabFile.classList.remove('is-active');
      tabFile.setAttribute('aria-selected', 'false');
      paneCamera.classList.add('is-active');
      paneCamera.hidden = false;
      paneFile.classList.remove('is-active');
      paneFile.hidden = true;
    } else {
      tabFile.classList.add('is-active');
      tabFile.setAttribute('aria-selected', 'true');
      tabCamera.classList.remove('is-active');
      tabCamera.setAttribute('aria-selected', 'false');
      paneFile.classList.add('is-active');
      paneFile.hidden = false;
      paneCamera.classList.remove('is-active');
      paneCamera.hidden = true;
      if (isCameraRunning) {
        stopCamera();
      }
    }
  }

  tabCamera?.addEventListener('click', () => switchTab('camera'));
  tabFile?.addEventListener('click', () => switchTab('file'));

  // Sound chime synthesizer (Web Audio API)
  function playScanChime() {
    if (!isSoundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(784, ctx.currentTime); // G5
      osc.frequency.exponentialRampToValueAtTime(1174.66, ctx.currentTime + 0.08); // D6
      gain.gain.setValueAtTime(0.14, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.14);
    } catch {}
  }

  // Haptic feedback
  function triggerHaptic() {
    if (navigator.vibrate) {
      try { navigator.vibrate([35, 40, 45]); } catch {}
    }
  }

  // Camera enumeration & management
  async function refreshCamerasList() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      availableCameras = videoDevices || [];

      if (availableCameras.length >= 2) {
        camSwitchBox.hidden = false;
        camSelect.innerHTML = '';

        availableCameras.forEach((device, index) => {
          const opt = document.createElement('option');
          opt.value = device.deviceId;
          let label = device.label || `${t('الكاميرا', 'Camera')} ${index + 1}`;
          const lower = label.toLowerCase();
          if (lower.includes('back') || lower.includes('rear') || lower.includes('environment')) {
            label = t('📷 الكاميرا الخلفية (Rear)', '📷 Rear Camera');
          } else if (lower.includes('front') || lower.includes('user') || lower.includes('facetime')) {
            label = t('🤳 الكاميرا الأمامية (Front)', '🤳 Front Camera');
          } else if (lower.includes('wide')) {
            label = t('📐 عدسة واسعة (Wide)', '📐 Wide Lens');
          }
          opt.textContent = label;
          if (selectedDeviceId && device.deviceId === selectedDeviceId) {
            opt.selected = true;
            currentCameraIndex = index;
            activeCamLabel.textContent = label;
          }
          camSelect.appendChild(opt);
        });

        if (!selectedDeviceId) {
          const rearIndex = availableCameras.findIndex(d => {
            const l = (d.label || '').toLowerCase();
            return l.includes('back') || l.includes('rear') || l.includes('environment');
          });
          if (rearIndex !== -1) {
            currentCameraIndex = rearIndex;
            selectedDeviceId = availableCameras[rearIndex].deviceId;
            camSelect.selectedIndex = rearIndex;
            activeCamLabel.textContent = camSelect.options[rearIndex].textContent;
          } else {
            activeCamLabel.textContent = camSelect.options[0]?.textContent || t('تبديل الكاميرا', 'Switch Camera');
          }
        }
      } else {
        camSwitchBox.hidden = true;
      }
    } catch (err) {
      console.warn('Could not list cameras:', err);
      camSwitchBox.hidden = true;
    }
  }

  // Check hardware torch capabilities
  function checkTorchSupport() {
    try {
      const track = mediaStream?.getVideoTracks()[0];
      if (track) {
        const capabilities = track.getCapabilities ? track.getCapabilities() : {};
        if ('torch' in capabilities) {
          btnTorch.hidden = false;
          return;
        }
      }
    } catch {}
    btnTorch.hidden = true;
  }

  async function setTorch(on) {
    try {
      const track = mediaStream?.getVideoTracks()[0];
      if (track && track.getCapabilities?.().torch) {
        await track.applyConstraints({ advanced: [{ torch: on }] });
        isTorchOn = on;
        btnTorch.classList.toggle('is-active', on);
      }
    } catch (err) {
      console.warn('Torch constraint error:', err);
    }
  }

  // Zoom Management (Hardware + Synchronized Canvas Digital Crop)
  function initZoomCapabilities() {
    const track = mediaStream?.getVideoTracks()[0];
    const capabilities = track?.getCapabilities ? track.getCapabilities() : {};

    if ('zoom' in capabilities) {
      hasHardwareZoom = true;
      minZoom = capabilities.zoom.min || 1;
      maxZoom = Math.min(capabilities.zoom.max || 4, 8);
      zoomStep = capabilities.zoom.step || 0.1;
    } else {
      hasHardwareZoom = false;
      minZoom = 1;
      maxZoom = 4;
      zoomStep = 0.2;
    }

    currentZoom = 1;
    applyZoom(1);
  }

  async function applyZoom(targetZoom) {
    const clamped = Math.max(minZoom, Math.min(maxZoom, targetZoom));
    currentZoom = Math.round(clamped * 10) / 10;

    if (hasHardwareZoom) {
      try {
        const track = mediaStream?.getVideoTracks()[0];
        if (track) {
          await track.applyConstraints({ advanced: [{ zoom: currentZoom }] });
        }
      } catch (err) {
        console.warn('Hardware zoom constraint failed, using visual fallback:', err);
        video.style.transform = currentZoom > 1 ? `scale(${currentZoom})` : '';
      }
    } else {
      video.style.transform = currentZoom > 1 ? `scale(${currentZoom})` : '';
    }
    video.style.transformOrigin = 'center center';

    updateZoomUI();
  }

  function updateZoomUI() {
    zoomPills.forEach(pill => {
      const z = parseFloat(pill.dataset.zoom);
      pill.classList.toggle('is-active', Math.abs(currentZoom - z) < 0.25);
    });
    if (zoomOutBtn) zoomOutBtn.disabled = currentZoom <= minZoom + 0.05;
    if (zoomInBtn) zoomInBtn.disabled = currentZoom >= maxZoom - 0.05;
  }

  // Zoom UI Event Listeners
  zoomPills.forEach(pill => {
    pill.addEventListener('click', () => {
      const target = parseFloat(pill.dataset.zoom);
      if (!isNaN(target)) applyZoom(target);
    });
  });

  zoomOutBtn?.addEventListener('click', () => applyZoom(currentZoom - (zoomStep || 0.5)));
  zoomInBtn?.addEventListener('click', () => applyZoom(currentZoom + (zoomStep || 0.5)));

  // Gesture: Pinch-To-Zoom on Viewfinder (Mobile)
  let initialPinchDistance = 0;
  let initialPinchZoom = 1;

  viewfinder?.addEventListener('touchstart', (e) => {
    if (e.touches.length === 2) {
      initialPinchDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      initialPinchZoom = currentZoom;
    }
  }, { passive: true });

  viewfinder?.addEventListener('touchmove', (e) => {
    if (e.touches.length === 2 && initialPinchDistance > 0) {
      const currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const factor = currentDistance / initialPinchDistance;
      applyZoom(initialPinchZoom * factor);
    }
  }, { passive: true });

  viewfinder?.addEventListener('touchend', (e) => {
    if (e.touches.length < 2) {
      initialPinchDistance = 0;
    }
  }, { passive: true });

  // Gesture: Double-Tap Smart Zoom (Toggle 1x <-> 2x)
  let lastTapTime = 0;
  viewfinder?.addEventListener('click', (e) => {
    if (!isCameraRunning) return;
    if (e.target.closest('#qr-hud-top') || e.target.closest('#qr-zoom-controls')) return;
    const now = Date.now();
    if (now - lastTapTime < 320) {
      applyZoom(currentZoom > 1.2 ? 1 : 2);
      lastTapTime = 0;
    } else {
      lastTapTime = now;
    }
  });

  // Gesture: Mouse Wheel Zoom (Desktop)
  viewfinder?.addEventListener('wheel', (e) => {
    if (!isCameraRunning) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.2 : -0.2;
    applyZoom(currentZoom + delta);
  }, { passive: false });

  // Start live scanning
  async function startCamera() {
    trackEvent('tool_started', 'qr-scanner');
    liveStatusText.textContent = t('جاري تشغيل الكاميرا...', 'Starting camera...');
    btnStart.disabled = true;

    try {
      // High-resolution constraints without overconstraining
      const constraints = {
        video: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          facingMode: selectedDeviceId ? undefined : { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : { facingMode: 'environment' }
        });
      }

      video.srcObject = mediaStream;
      await video.play();

      isCameraRunning = true;
      viewfinder?.classList.add('is-streaming');
      cameraSplash.hidden = true;
      video.hidden = false;
      reticle.hidden = false;
      hudTop.hidden = false;
      hudBottom.hidden = false;

      // Show zoom controls ONLY when streaming
      if (zoomControls) {
        zoomControls.hidden = false;
        zoomControls.style.display = '';
      }

      liveStatusText.textContent = t('جاري المسح... وجّه الكاميرا نحو أي كود QR أو باركود', 'Scanning... Point camera at any QR code or barcode');
      btnStart.disabled = false;

      await refreshCamerasList();
      checkTorchSupport();
      initZoomCapabilities();
      startContinuousScanLoop();
    } catch (error) {
      console.error('Camera startup error:', error);
      stopCamera();
      btnStart.disabled = false;
      const isPermissionDenied = error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError';
      const msg = isPermissionDenied
        ? t('يرجى السماح للمتصفح بالوصول إلى الكاميرا من إعدادات المتصفح أو القفل بجانب شريط العنوان.', 'Please grant camera permission in your browser address bar to scan.')
        : t('تعذر فتح الكاميرا. قد تكون مستخدمة من تطبيق آخر، أو يمكنك تجربة مسح الرمز من صورة.', 'Could not start camera. It may be in use by another app, or try scanning from an image.');

      showAlert(msg, { type: 'warning', title: t('تنبيه الكاميرا', 'Camera Notice') });
      trackEvent('tool_failed', 'qr-scanner');
    }
  }

  // Continuous Multi-Stage Detection Loop:
  // 1. Native BarcodeDetector (fastest hardware recognition when available)
  // 2. ZXing on full frame (captures all 1D Barcodes & standard QR codes)
  // 3. ZXing on magnified center crop (Smart Zoom: captures tiny/distant QR codes & zoomed views)
  function startContinuousScanLoop() {
    async function scanTick() {
      if (!isCameraRunning || !video || video.readyState < 2) {
        if (isCameraRunning) scanTimer = setTimeout(scanTick, 90);
        return;
      }

      if (isScanTickBusy) {
        if (isCameraRunning) scanTimer = setTimeout(scanTick, 50);
        return;
      }

      isScanTickBusy = true;
      try {
        const vw = video.videoWidth;
        const vh = video.videoHeight;

        if (vw > 0 && vh > 0) {
          let detected = null;

          // Stage 1: Native BarcodeDetector on video element
          if (nativeDetector) {
            try {
              const results = await nativeDetector.detect(video);
              if (results && results.length > 0) {
                const b = results[0];
                detected = {
                  getText: () => b.rawValue,
                  getBarcodeFormat: () => (b.format || 'BARCODE').toUpperCase().replace(/-/g, '_')
                };
              }
            } catch {}
          }

          // Stage 2: Full-frame canvas (1280px wide) with ZXing (Full 1D Barcodes & Large QR codes)
          if (!detected) {
            try {
              const targetW = Math.min(vw, 1280);
              const targetH = Math.round(targetW * (vh / vw));
              if (fullCanvas.width !== targetW || fullCanvas.height !== targetH) {
                fullCanvas.width = targetW;
                fullCanvas.height = targetH;
              }
              fullCtx.drawImage(video, 0, 0, vw, vh, 0, 0, targetW, targetH);
              detected = reader.decodeFromCanvas(fullCanvas);
            } catch {}
          }

          // Stage 3: Center-Crop Magnified Canvas with ZXing (Smart Zoom for Distant & Small Codes)
          if (!detected) {
            try {
              // Calculate center crop area (takes the reticle region and blows it up)
              const effectiveZoom = hasHardwareZoom ? 1 : Math.max(1, currentZoom);
              const cropFraction = (currentZoom > 1.2 ? 0.35 : 0.45) / (hasHardwareZoom ? 1 : (currentZoom > 1.2 ? currentZoom / 1.5 : 1));
              const cropSize = Math.round(Math.min(vw, vh) * cropFraction);
              const cropX = Math.round((vw - cropSize) / 2);
              const cropY = Math.round((vh - cropSize) / 2);

              if (zoomCanvas.width !== 720 || zoomCanvas.height !== 720) {
                zoomCanvas.width = 720;
                zoomCanvas.height = 720;
              }
              // Draw cropped center region scaled up to 720x720 (magnifies small codes)
              zoomCtx.drawImage(video, cropX, cropY, cropSize, cropSize, 0, 0, 720, 720);

              if (nativeDetector) {
                try {
                  const results = await nativeDetector.detect(zoomCanvas);
                  if (results && results.length > 0) {
                    const b = results[0];
                    detected = {
                      getText: () => b.rawValue,
                      getBarcodeFormat: () => (b.format || 'BARCODE').toUpperCase().replace(/-/g, '_')
                    };
                  }
                } catch {}
              }

              if (!detected) {
                detected = reader.decodeFromCanvas(zoomCanvas);
              }
            } catch {}
          }

          if (detected) {
            handleDetectedCode(detected);
          }
        }
      } catch (err) {
        // Continue scanning silently
      } finally {
        isScanTickBusy = false;
      }

      if (isCameraRunning) {
        scanTimer = setTimeout(scanTick, 90);
      }
    }

    scanTick();
  }

  // Stop camera
  function stopCamera() {
    isCameraRunning = false;
    isScanTickBusy = false;
    if (scanTimer) {
      clearTimeout(scanTimer);
      scanTimer = null;
    }

    if (mediaStream) {
      try {
        mediaStream.getTracks().forEach(t => t.stop());
      } catch {}
      mediaStream = null;
    }

    if (video) {
      video.srcObject = null;
      video.style.transform = '';
      video.hidden = true;
    }

    isTorchOn = false;
    currentZoom = 1;
    btnTorch.classList.remove('is-active');
    btnTorch.hidden = true;
    reticle.hidden = true;
    hudTop.hidden = true;
    hudBottom.hidden = true;

    // Strictly hide zoom controls
    if (zoomControls) {
      zoomControls.hidden = true;
      zoomControls.style.display = 'none';
    }

    cameraSplash.hidden = false;
    viewfinder?.classList.remove('is-streaming');
  }

  // Camera switch actions
  async function switchCameraTo(deviceId, label = '') {
    selectedDeviceId = deviceId;
    localStorage.setItem('kz_qr_camera_id', deviceId);
    if (label) activeCamLabel.textContent = label;

    if (isCameraRunning) {
      stopCamera();
      await startCamera();
    }
  }

  btnSwitchCam?.addEventListener('click', () => {
    if (availableCameras.length < 2) return;
    currentCameraIndex = (currentCameraIndex + 1) % availableCameras.length;
    const nextCam = availableCameras[currentCameraIndex];
    const nextLabel = camSelect.options[currentCameraIndex]?.textContent || '';
    camSelect.selectedIndex = currentCameraIndex;
    switchCameraTo(nextCam.deviceId, nextLabel);
  });

  camSelect?.addEventListener('change', (e) => {
    const selected = camSelect.options[camSelect.selectedIndex];
    switchCameraTo(e.target.value, selected?.textContent || '');
  });

  btnTorch?.addEventListener('click', () => setTorch(!isTorchOn));

  btnSound?.addEventListener('click', () => {
    isSoundEnabled = !isSoundEnabled;
    btnSound.classList.toggle('is-active', isSoundEnabled);
    btnSound.style.opacity = isSoundEnabled ? '1' : '0.45';
  });

  btnStart?.addEventListener('click', startCamera);
  btnStop?.addEventListener('click', stopCamera);

  // File Dropzone handling (Generalizing the PDF Tools Dropzone Design)
  dropzone?.addEventListener('click', () => fileInput?.click());
  dropzone?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput?.click();
    }
  });

  dropzone?.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover', 'is-dragging');
  });

  dropzone?.addEventListener('dragleave', () => {
    dropzone.classList.remove('dragover', 'is-dragging');
  });

  dropzone?.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover', 'is-dragging');
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImageFile(file);
  });

  fileInput?.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file);
  });

  btnResetFile?.addEventListener('click', () => {
    filePreviewCard.hidden = true;
    dropzone.hidden = false;
    fileInput.value = '';
    resultCard.hidden = true;
    if (currentFileUrl) {
      URL.revokeObjectURL(currentFileUrl);
      currentFileUrl = null;
    }
  });

  // Global clipboard paste listener
  window.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          switchTab('file');
          handleImageFile(file);
          break;
        }
      }
    }
  });

  // Decode from file (Dual Engine + Multi-scale enhancement for barcodes & small QR codes)
  async function handleImageFile(file) {
    trackEvent('tool_started', 'qr-scanner');
    dropzone.hidden = true;
    filePreviewCard.hidden = false;
    previewFilename.textContent = file.name;
    previewFilesize.textContent = `${(file.size / 1024).toFixed(1)} KB`;

    if (currentFileUrl) URL.revokeObjectURL(currentFileUrl);
    currentFileUrl = URL.createObjectURL(file);
    previewImg.src = currentFileUrl;

    let detected = null;

    // Load image element
    const img = new Image();
    img.src = currentFileUrl;
    try {
      await img.decode();
    } catch {}

    // Pass 1: Try Native BarcodeDetector on image
    if (nativeDetector) {
      try {
        const results = await nativeDetector.detect(img);
        if (results && results.length > 0) {
          const b = results[0];
          detected = {
            getText: () => b.rawValue,
            getBarcodeFormat: () => (b.format || 'BARCODE').toUpperCase().replace(/-/g, '_')
          };
        }
      } catch {}
    }

    // Pass 2: Draw full image to fullCanvas and decode with ZXing
    if (!detected && img.naturalWidth > 0 && img.naturalHeight > 0) {
      try {
        fullCanvas.width = img.naturalWidth;
        fullCanvas.height = img.naturalHeight;
        fullCtx.drawImage(img, 0, 0);
        detected = reader.decodeFromCanvas(fullCanvas);
      } catch {}
    }

    // Pass 3: Try ZXing decodeFromImageUrl
    if (!detected) {
      try {
        detected = await reader.decodeFromImageUrl(currentFileUrl);
      } catch {}
    }

    // Pass 4: Try Center-Crop Magnified Canvas for small codes in large photos
    if (!detected && img.naturalWidth > 0 && img.naturalHeight > 0) {
      try {
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const cropSize = Math.round(Math.min(iw, ih) * 0.5);
        const cropX = Math.round((iw - cropSize) / 2);
        const cropY = Math.round((ih - cropSize) / 2);

        zoomCanvas.width = 800;
        zoomCanvas.height = 800;
        zoomCtx.drawImage(img, cropX, cropY, cropSize, cropSize, 0, 0, 800, 800);

        if (nativeDetector) {
          try {
            const results = await nativeDetector.detect(zoomCanvas);
            if (results && results.length > 0) {
              const b = results[0];
              detected = {
                getText: () => b.rawValue,
                getBarcodeFormat: () => (b.format || 'BARCODE').toUpperCase().replace(/-/g, '_')
              };
            }
          } catch {}
        }

        if (!detected) {
          detected = reader.decodeFromCanvas(zoomCanvas);
        }
      } catch {}
    }

    if (detected) {
      handleDetectedCode(detected);
      trackEvent('tool_completed', 'qr-scanner');
    } else {
      const msg = t('لم يُعثر على رمز QR أو باركود واضح في الصورة. يرجى التأكد من وضوح الصورة وتجربة زاوية أخرى.', 'No readable QR code or barcode found in this image. Please ensure the code is clear and well-lit.');
      showToast(msg, { type: 'warning' });
      trackEvent('tool_failed', 'qr-scanner');
    }
  }

  // Handle successful detection (camera or file)
  function handleDetectedCode(result) {
    if (!result) return;
    const text = result.getText();
    const format = String(result.getBarcodeFormat() || 'QR_CODE').toUpperCase();

    // Debounce duplicate scans within 1.5 seconds
    const now = Date.now();
    if (text === lastScannedText && now - lastScannedTime < 1500) {
      return;
    }
    lastScannedText = text;
    lastScannedTime = now;

    playScanChime();
    triggerHaptic();

    // Render Smart Result
    renderSmartResult(text, format);
    addToHistory(text, format);
    trackEvent('tool_completed', 'qr-scanner');

    // Scroll smoothly to result card
    resultCard.hidden = false;
    resultCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // Smart Content Classifier & Parser (Enhanced with 1D Barcode Detection)
  function parseQrContent(raw, format = '') {
    const text = String(raw || '').trim();
    const isOneDBarcode = /^(EAN|UPC|CODE_128|CODE_39|CODE_93|ITF|CODABAR)/i.test(format);

    // 1. Wi-Fi
    if (text.startsWith('WIFI:')) {
      const getW = (key) => {
        const m = text.match(new RegExp(`${key}:([^;]*);`));
        return m ? m[1] : '';
      };
      const ssid = getW('S');
      const type = getW('T') || 'WPA';
      const pass = getW('P');
      const hidden = getW('H') === 'true';
      return {
        type: 'wifi',
        title: t('شبكة واي فاي (Wi-Fi)', 'Wi-Fi Network'),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>`,
        data: { ssid, type, pass, hidden, raw: text }
      };
    }

    // 2. vCard / MeCard Contact
    if (text.startsWith('BEGIN:VCARD') || text.startsWith('MECARD:')) {
      let name = '';
      let phone = '';
      let email = '';
      let org = '';

      if (text.startsWith('BEGIN:VCARD')) {
        const fn = text.match(/(?:^|\n)FN[^\:]*:(.*)/i);
        const n = text.match(/(?:^|\n)N[^\:]*:(.*)/i);
        name = fn ? fn[1].trim() : n ? n[1].replace(/;/g, ' ').trim() : '';
        const tel = text.match(/(?:^|\n)TEL[^\:]*:(.*)/i);
        phone = tel ? tel[1].trim() : '';
        const em = text.match(/(?:^|\n)EMAIL[^\:]*:(.*)/i);
        email = em ? em[1].trim() : '';
        const o = text.match(/(?:^|\n)ORG[^\:]*:(.*)/i);
        org = o ? o[1].trim() : '';
      } else {
        const getN = text.match(/N:([^;]*);?/i);
        name = getN ? getN[1].trim() : '';
        const getT = text.match(/TEL:([^;]*);?/i);
        phone = getT ? getT[1].trim() : '';
        const getE = text.match(/EMAIL:([^;]*);?/i);
        email = getE ? getE[1].trim() : '';
      }

      return {
        type: 'contact',
        title: t('بطاقة جهة اتصال (vCard)', 'Contact Card (vCard)'),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
        data: { name: name || t('جهة اتصال بدون اسم', 'Unnamed Contact'), phone, email, org, raw: text }
      };
    }

    // 3. Web URL
    let urlObj = null;
    try {
      if (/^https?:\/\//i.test(text)) {
        urlObj = new URL(text);
      } else if (/^www\.[a-z0-9\-]+(\.[a-z]{2,})+/i.test(text)) {
        urlObj = new URL('https://' + text);
      }
    } catch {}

    if (urlObj) {
      return {
        type: 'url',
        title: t('رابط موقع إلكتروني (URL)', 'Website Link (URL)'),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
        data: {
          url: urlObj.href,
          hostname: urlObj.hostname,
          isHttps: urlObj.protocol === 'https:',
          raw: text
        }
      };
    }

    // 4. Email
    if (text.toLowerCase().startsWith('mailto:') || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(text)) {
      let email = text.replace(/^mailto:/i, '').split('?')[0];
      const subjectMatch = text.match(/[?&]subject=([^&]*)/i);
      const subject = subjectMatch ? decodeURIComponent(subjectMatch[1]) : '';
      return {
        type: 'email',
        title: t('عنوان بريد إلكتروني (Email)', 'Email Address'),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>`,
        data: { email, subject, raw: text }
      };
    }

    // 5. Phone
    if (text.toLowerCase().startsWith('tel:') || (/^(\+?[0-9]{1,4}[\s-]?)?(\(?[0-9]{2,5}\)?[\s-]?)?[0-9]{5,12}$/.test(text) && text.length >= 7 && !isOneDBarcode)) {
      const phone = text.replace(/^tel:/i, '').trim();
      return {
        type: 'phone',
        title: t('رقم هاتف (Phone Number)', 'Phone Number'),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
        data: { phone, raw: text }
      };
    }

    // 6. SMS
    if (text.toLowerCase().startsWith('sms:')) {
      const parts = text.slice(4).split('?');
      const phone = parts[0];
      const bodyMatch = text.match(/[?&]body=([^&]*)/i);
      const body = bodyMatch ? decodeURIComponent(bodyMatch[1]) : '';
      return {
        type: 'sms',
        title: t('رسالة نصية (SMS)', 'SMS Message'),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
        data: { phone, body, raw: text }
      };
    }

    // 7. Geo Location
    if (text.toLowerCase().startsWith('geo:')) {
      const coords = text.slice(4).split('?')[0].split(',');
      if (coords.length >= 2) {
        return {
          type: 'geo',
          title: t('موقع جغرافي (Location)', 'Geographic Location'),
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
          data: { lat: coords[0].trim(), lng: coords[1].trim(), raw: text }
        };
      }
    }

    // 8. 1D Product Barcode (EAN, UPC, Code 128, Code 39, ITF, Codabar)
    if (isOneDBarcode || /^[0-9]{8,14}$/.test(text)) {
      return {
        type: 'barcode',
        title: t(`باركود تجاري (${format || 'BARCODE'})`, `Product Barcode (${format || 'BARCODE'})`),
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 5v14M6 5v14M8 5v14M11 5v14M14 5v14M17 5v14M21 5v14"/></svg>`,
        data: { code: text, format: format || 'BARCODE', raw: text }
      };
    }

    // 9. Plain Text / Code / Fallback
    return {
      type: 'text',
      title: t('نص عادي أو كود', 'Plain Text or Code'),
      icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>`,
      data: { raw: text }
    };
  }

  // Render Smart Result Card
  function renderSmartResult(rawText, format) {
    const parsed = parseQrContent(rawText, format);

    typeIcon.innerHTML = parsed.icon;
    typeTitle.textContent = parsed.title;
    formatPill.textContent = format;

    resultContent.innerHTML = '';
    resultActions.innerHTML = '';

    // Render specialized content card
    if (parsed.type === 'url') {
      resultContent.innerHTML = `
        <div class="qr-url-box">
          <div class="qr-url-top">
            <span class="qr-domain-badge">${parsed.data.hostname}</span>
            ${parsed.data.isHttps ? `<span class="qr-https-badge">🔒 HTTPS ${t('آمن', 'Secure')}</span>` : ''}
          </div>
          <a href="${parsed.data.url}" target="_blank" rel="noopener noreferrer" class="qr-url-link">
            <span>${parsed.data.url}</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
      `;

      resultActions.innerHTML = `
        <a href="${parsed.data.url}" target="_blank" rel="noopener noreferrer" class="btn-primary qr-action-btn">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          <span>${t('فتح الرابط في نافذة جديدة', 'Open Link in New Tab')}</span>
        </a>
        <button type="button" class="btn-secondary qr-action-btn" id="qr-act-copy">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <span>${t('نسخ الرابط', 'Copy Link')}</span>
        </button>
      `;
    } else if (parsed.type === 'barcode') {
      resultContent.innerHTML = `
        <div class="qr-barcode-box">
          <div class="qr-barcode-top">
            <span class="qr-barcode-format-tag">${parsed.data.format}</span>
            <span class="qr-text-meta">${parsed.data.code.length} ${t('أرقام / خانات', 'digits')}</span>
          </div>
          <div class="qr-barcode-number">${parsed.data.code}</div>
        </div>
      `;

      resultActions.innerHTML = `
        <a href="https://www.google.com/search?q=${encodeURIComponent(parsed.data.code)}" target="_blank" rel="noopener noreferrer" class="btn-primary qr-action-btn">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <span>${t('بحث عن المنتج في Google', 'Search Product on Google')}</span>
        </a>
        <button type="button" class="btn-secondary qr-action-btn" id="qr-act-copy">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <span>${t('نسخ رقم الباركود', 'Copy Barcode')}</span>
        </button>
      `;
    } else if (parsed.type === 'wifi') {
      resultContent.innerHTML = `
        <div class="qr-wifi-box">
          <div class="qr-wifi-row">
            <span class="qr-info-label">${t('اسم الشبكة (SSID):', 'Network Name (SSID):')}</span>
            <strong class="qr-info-val">${parsed.data.ssid || t('غير محدد', 'Unknown')}</strong>
          </div>
          <div class="qr-wifi-row">
            <span class="qr-info-label">${t('نوع الحماية:', 'Security Type:')}</span>
            <span class="qr-info-pill">${parsed.data.type || 'WPA'}</span>
          </div>
          ${parsed.data.pass ? `
            <div class="qr-wifi-row qr-pass-row">
              <span class="qr-info-label">${t('كلمة المرور:', 'Password:')}</span>
              <div class="qr-pass-wrap">
                <input type="password" readonly id="qr-wifi-pass-input" value="${parsed.data.pass}" class="qr-pass-input">
                <button type="button" class="btn-tertiary btn-xs" id="qr-btn-toggle-pass" title="${t('إظهار / إخفاء', 'Toggle Visibility')}">👁️</button>
              </div>
            </div>
          ` : `<p class="qr-wifi-open">${t('هذه الشبكة مفتوحة بدون كلمة مرور.', 'This network is open with no password.')}</p>`}
        </div>
      `;

      resultActions.innerHTML = `
        ${parsed.data.pass ? `
          <button type="button" class="btn-primary qr-action-btn" id="qr-act-copy-pass">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            <span>${t('نسخ كلمة المرور', 'Copy Password')}</span>
          </button>
        ` : ''}
        <button type="button" class="btn-secondary qr-action-btn" id="qr-act-copy">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <span>${t('نسخ بيانات الشبكة', 'Copy Network Info')}</span>
        </button>
      `;

      const passInput = document.getElementById('qr-wifi-pass-input');
      const togglePassBtn = document.getElementById('qr-btn-toggle-pass');
      togglePassBtn?.addEventListener('click', () => {
        if (passInput) {
          passInput.type = passInput.type === 'password' ? 'text' : 'password';
        }
      });
      document.getElementById('qr-act-copy-pass')?.addEventListener('click', () => {
        copyAndNotify(parsed.data.pass, t('تم نسخ كلمة المرور بنجاح!', 'Password copied to clipboard!'));
      });
    } else if (parsed.type === 'contact') {
      resultContent.innerHTML = `
        <div class="qr-contact-box">
          <div class="qr-contact-name">${parsed.data.name}</div>
          ${parsed.data.org ? `<div class="qr-contact-org">${parsed.data.org}</div>` : ''}
          ${parsed.data.phone ? `<div class="qr-contact-row"><span>📞</span> <a href="tel:${parsed.data.phone}">${parsed.data.phone}</a></div>` : ''}
          ${parsed.data.email ? `<div class="qr-contact-row"><span>✉️</span> <a href="mailto:${parsed.data.email}">${parsed.data.email}</a></div>` : ''}
        </div>
      `;

      resultActions.innerHTML = `
        ${parsed.data.phone ? `
          <a href="tel:${parsed.data.phone}" class="btn-primary qr-action-btn">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            <span>${t('اتصال بالرقم', 'Call Contact')}</span>
          </a>
        ` : ''}
        <button type="button" class="btn-secondary qr-action-btn" id="qr-act-copy">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <span>${t('نسخ بيانات الاتصال', 'Copy Contact Info')}</span>
        </button>
      `;
    } else {
      // Plain text or fallback
      const wordCount = (parsed.data.raw.match(/\S+/g) || []).length;
      const charCount = parsed.data.raw.length;
      resultContent.innerHTML = `
        <div class="qr-text-box">
          <div class="qr-text-meta">
            <span>${charCount} ${t('حرف', 'characters')}</span> · <span>${wordCount} ${t('كلمة', 'words')}</span>
          </div>
          <pre class="qr-text-pre" id="qr-raw-text">${parsed.data.raw}</pre>
        </div>
      `;

      resultActions.innerHTML = `
        <button type="button" class="btn-primary qr-action-btn" id="qr-act-copy">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <span>${t('نسخ النص', 'Copy Text')}</span>
        </button>
      `;
    }

    // Common universal actions (Scan Again & Share)
    const scanAgainBtn = document.createElement('button');
    scanAgainBtn.type = 'button';
    scanAgainBtn.className = 'btn-secondary qr-action-btn';
    scanAgainBtn.innerHTML = `
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>
      <span>${t('مسح رمز آخر', 'Scan Next Code')}</span>
    `;
    scanAgainBtn.addEventListener('click', () => {
      resultCard.hidden = true;
      if (tabCamera.classList.contains('is-active') && !isCameraRunning) {
        startCamera();
      }
    });

    if (navigator.share) {
      const shareBtn = document.createElement('button');
      shareBtn.type = 'button';
      shareBtn.className = 'btn-tertiary qr-action-btn';
      shareBtn.innerHTML = `
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
        <span>${t('مشاركة', 'Share')}</span>
      `;
      shareBtn.addEventListener('click', () => {
        navigator.share({ text: rawText }).catch(() => {});
      });
      resultActions.appendChild(shareBtn);
    }

    resultActions.appendChild(scanAgainBtn);

    // Bind generic copy action
    document.getElementById('qr-act-copy')?.addEventListener('click', () => {
      copyAndNotify(rawText, t('تم نسخ النتيجة بنجاح إلى الحافظة!', 'Result copied to clipboard!'));
    });
  }

  // Copy helper with feedback
  async function copyAndNotify(text, successMsg) {
    try {
      await copyText(text);
      showToast(successMsg, { type: 'success' });
    } catch {
      showToast(t('تعذر النسخ إلى الحافظة تلقائياً.', 'Could not copy to clipboard.'), { type: 'error' });
    }
  }

  // Session History Management
  function addToHistory(text, format) {
    if (historyItems.some(i => i.text === text)) return;
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    historyItems.unshift({ text, format, time: timeStr });
    if (historyItems.length > 10) historyItems.pop();

    renderHistory();
  }

  function renderHistory() {
    if (!historyItems.length) {
      historyTray.hidden = true;
      return;
    }
    historyTray.hidden = false;
    historyBadge.textContent = String(historyItems.length);
    historyList.innerHTML = '';

    historyItems.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'qr-history-item';
      const parsed = parseQrContent(item.text, item.format);
      li.innerHTML = `
        <div class="qr-hist-icon" aria-hidden="true">${parsed.icon}</div>
        <div class="qr-hist-body">
          <div class="qr-hist-text">${escapeHtml(item.text)}</div>
          <div class="qr-hist-time">${item.time} · ${item.format}</div>
        </div>
        <button type="button" class="btn-tertiary btn-xs qr-hist-copy-btn" title="${t('نسخ', 'Copy')}">
          ${t('نسخ', 'Copy')}
        </button>
      `;
      li.querySelector('.qr-hist-copy-btn').addEventListener('click', () => {
        copyAndNotify(item.text, t('تم نسخ الرمز من السجل.', 'Copied from history.'));
      });
      historyList.appendChild(li);
    });
  }

  btnClearHistory?.addEventListener('click', () => {
    historyItems.length = 0;
    renderHistory();
  });

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  // Cleanup on page hide
  window.addEventListener('pagehide', () => {
    stopCamera();
    if (currentFileUrl) URL.revokeObjectURL(currentFileUrl);
  });
}
